import React, { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Grid, Paper, Skeleton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MovieFilterIcon from "@mui/icons-material/MovieFilter";
import { toast } from "react-toastify";
import { isVreDocument } from "@wooglobe/vre-editor";
import api from "../../../conf/api";

const ACCENTS = ["#4338ca", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4"];

export const apiErrorText = (err, fallback) =>
  err?.response?.data?.error || err?.response?.data?.detail || err?.message || fallback;

/** GET one template with its document: `{id, name, version, document, _fallback?}`. */
export async function fetchEditorTemplate(id) {
  const res = await api.get(`/mrss/editor-templates/${encodeURIComponent(id)}/`);
  return res.data;
}

/** The shape the dialog works with, from a template detail response. */
export const toEditorTemplate = (detail, summary = {}) => ({
  id: String(detail?.id ?? summary.id ?? ""),
  name: detail?.name || summary.name || "Template",
  version: typeof detail?.version === "number" ? detail.version : null,
  document: detail?.document,
  fallback: Boolean(detail?._fallback),
});

/**
 * Step 0 of the video editor: the template gallery.
 *
 * Picking a card loads that template's document and hands it to `onSelect`
 * only when it is a document the editor can open.
 */
export default function EditorTemplateGallery({ selectedId, onSelect, notice }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pickingId, setPickingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get("/mrss/editor-templates/")
      .then((res) => {
        if (!cancelled) setTemplates(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorText(err, "Request failed"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePick = async (summary) => {
    if (pickingId) return;
    setPickingId(summary.id);
    try {
      const detail = await fetchEditorTemplate(summary.id);
      if (!isVreDocument(detail?.document)) {
        toast.error(`"${summary.name}" is not a Video Editor template and cannot be opened.`);
        return;
      }
      if (detail._fallback) toast.info(`Using the stored copy of "${summary.name}".`);
      onSelect(toEditorTemplate(detail, summary));
    } catch (err) {
      toast.error(`Could not load "${summary.name}": ${apiErrorText(err, "request failed")}`);
    } finally {
      setPickingId(null);
    }
  };

  return (
    <Box>
      {notice && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          {notice}
        </Alert>
      )}

      <Box mb={2.5} p={2} sx={{ bgcolor: "white", borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Typography variant="subtitle1" fontWeight={700} color="#312e81" gutterBottom>
          Choose a template
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The template sets the branding, caption styles and layout. Your video is placed into it, and
          everything can be adjusted in the editor on the next step.
        </Typography>
      </Box>

      {loading ? (
        <Grid container spacing={2}>
          {[...Array(8)].map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={190} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Could not load templates ({error}). Make sure <strong>VRE_API_URL</strong> and{" "}
          <strong>VRE_API_KEY</strong> are set in the server environment.
        </Alert>
      ) : templates.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No templates found. Create one in the video studio first — if you expected templates here, check{" "}
          <strong>VRE_API_URL</strong> and <strong>VRE_API_KEY</strong> on the server.
        </Alert>
      ) : (
        <Grid container spacing={1.5}>
          {templates.map((tmpl, idx) => {
            const isSelected = selectedId === String(tmpl.id);
            const accent = ACCENTS[idx % ACCENTS.length];
            return (
              <Grid item xs={12} sm={6} md={3} key={tmpl.id}>
                <Paper
                  onClick={() => handlePick(tmpl)}
                  elevation={isSelected ? 3 : 0}
                  sx={{
                    cursor: pickingId ? "progress" : "pointer",
                    border: "2px solid",
                    borderColor: isSelected ? accent : "#e2e8f0",
                    overflow: "hidden",
                    borderRadius: 2,
                    transition: "all 0.15s",
                    bgcolor: isSelected ? alpha(accent, 0.04) : "white",
                    position: "relative",
                    "&:hover": { boxShadow: 3, borderColor: alpha(accent, 0.7), transform: "translateY(-1px)" },
                  }}
                >
                  <Box sx={{ height: 4, bgcolor: accent, opacity: isSelected ? 1 : 0.35 }} />
                  <Box
                    sx={{
                      height: 140,
                      bgcolor: "#0f172a",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    {tmpl.thumbnail_url ? (
                      <Box
                        component="img"
                        src={tmpl.thumbnail_url}
                        alt=""
                        loading="lazy"
                        sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
                      />
                    ) : (
                      <MovieFilterIcon sx={{ fontSize: 36, color: alpha(accent, 0.7) }} />
                    )}
                    {pickingId === tmpl.id && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          bgcolor: "rgba(15,23,42,0.55)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress size={28} sx={{ color: "white" }} />
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                    <Box flex={1} minWidth={0}>
                      <Typography
                        variant="body2"
                        fontWeight={isSelected ? 700 : 500}
                        noWrap
                        sx={{ color: isSelected ? accent : "text.primary" }}
                      >
                        {tmpl.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tmpl.width && tmpl.height ? `${tmpl.width}×${tmpl.height}` : "Unknown size"}
                        {tmpl.version ? ` · v${tmpl.version}` : ""}
                      </Typography>
                    </Box>
                    {isSelected && <CheckCircleIcon sx={{ fontSize: 18, color: accent, flexShrink: 0 }} />}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
